import { useMemo } from 'react';
import VisualizationViewer from '../../components/magnified/VisualizationViewer';
import ResultsSummary from '../../components/magnified/ResultsSummary';
import CategoryInsightCard from '../../components/magnified/CategoryInsightCard';
import { buildCategoryInsights } from '../../components/magnified/utils/categoryInsights';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './MagnifiedAnalysis.css';

function resolveOverlayUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('data:')) return url;

  const uploadsIdx = url.indexOf('/uploads/');
  if (uploadsIdx >= 0) {
    const path = url.slice(uploadsIdx);
    const base = (getApiBaseUrl() || '').replace(/\/$/, '');
    return base ? `${base}${path}` : path;
  }

  if (url.startsWith('http')) return url;
  const base = (getApiBaseUrl() || '').replace(/\/$/, '');
  if (!base) return url;
  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
}

function resolveOverlays(overlays) {
  if (!overlays || typeof overlays !== 'object') return overlays;
  return Object.fromEntries(
    Object.entries(overlays).map(([k, v]) => [k, resolveOverlayUrl(v)])
  );
}

export default function MagnifiedAnalysisSection({ magnifiedAnalysis }) {
  const results = useMemo(() => {
    if (!magnifiedAnalysis) return null;
    return {
      ...magnifiedAnalysis,
      overlays: resolveOverlays(magnifiedAnalysis.overlays),
    };
  }, [magnifiedAnalysis]);

  if (!results?.metrics) {
    return (
      <section className="magnified-analysis magnified-analysis--empty">
        <p className="magnified-analysis__unavailable">
          Magnified scalp analysis is not available for this scan.
        </p>
      </section>
    );
  }

  const insights = buildCategoryInsights(results.metrics);

  return (
    <section className="magnified-analysis">
      <header className="magnified-analysis__header">
        <div>
          <h2 className="magnified-analysis__title">Magnified Scalp Analysis</h2>
          <p className="magnified-analysis__meta">
            {`Completed in ${Math.round((results.processing_time_ms || 0) / 1000)} seconds`}
          </p>
        </div>
      </header>

      <VisualizationViewer overlays={results.overlays} annotations={results.annotations} />

      <ResultsSummary insights={insights} />

      <div className="dashboard__insights">
        <h3>What We Found</h3>
        <div className="dashboard__insights-grid">
          {insights.map((item) => (
            <CategoryInsightCard key={item.id} {...item} />
          ))}
        </div>
      </div>
    </section>
  );
}
